import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2,
  AlertCircle,
  Plus,
  CalendarDays,
  MapPin,
  Users,
  X,
  Trash2,
  Mail,
  Phone,
} from "lucide-react";
import {
  adminListSessions,
  adminCreateSession,
  adminDeleteSession,
} from "../lib/api";

function pad(n) { return String(n).padStart(2, "0"); }
function toLocalInput(dt) {
  const d = new Date(dt);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function defaultStart() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return toLocalInput(d);
}
function defaultEnd() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toLocalInput(d);
}

function CreateSessionModal({ pin, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    type: "one_on_one",
    start_at: defaultStart(),
    end_at: defaultEnd(),
    capacity: 1,
    location: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await adminCreateSession(pin, {
        title: form.title.trim(),
        type: form.type,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        capacity: form.type === "group" ? Number(form.capacity) || 1 : 1,
        location: form.location.trim() || null,
        notes: form.notes.trim() || null,
      });
      onCreated();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not create session";
      setError(typeof detail === "string" ? detail : "Could not create session");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--lc-ink)]/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-xl bg-white border border-[var(--lc-line)] rounded-md p-6 lg:p-8"
        data-testid="session-create-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="eyebrow text-[var(--lc-moss)]">Schedule a session</div>
            <h3 className="font-serif-display text-2xl mt-1">New slot</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--lc-muted)] hover:text-red-700" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-4">
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Title</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Monday mobility, 1:1 coaching check-in..."
              className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
              data-testid="session-create-title"
            />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm"
                data-testid="session-create-type"
              >
                <option value="one_on_one">1 : 1</option>
                <option value="group">Group</option>
              </select>
            </div>
            <div className={form.type === "group" ? "" : "opacity-50 pointer-events-none"}>
              <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Capacity</label>
              <input
                type="number"
                min={1}
                max={50}
                value={form.capacity}
                onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm"
                data-testid="session-create-capacity"
              />
            </div>
            <div>
              <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Location</label>
              <input
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                placeholder="Studio / Zoom"
                className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm"
                data-testid="session-create-location"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Start</label>
              <input
                required
                type="datetime-local"
                value={form.start_at}
                onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm"
                data-testid="session-create-start"
              />
            </div>
            <div>
              <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">End</label>
              <input
                required
                type="datetime-local"
                value={form.end_at}
                onChange={(e) => setForm((f) => ({ ...f, end_at: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm"
                data-testid="session-create-end"
              />
            </div>
          </div>
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Notes (visible to members)</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="What to bring, what to expect..."
              className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm resize-none"
              data-testid="session-create-notes"
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.14em] uppercase hover:border-[var(--lc-ink)]">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !form.title.trim()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)] disabled:opacity-50"
            data-testid="session-create-submit"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create session
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminSessionsPanel({ pin }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [includePast, setIncludePast] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSessions(await adminListSessions(pin, includePast));
    } finally {
      setLoading(false);
    }
  }, [pin, includePast]);

  useEffect(() => {
    load();
  }, [load]);

  const totalRsvps = useMemo(
    () => sessions.reduce((s, x) => s + (x.rsvp_count || 0), 0),
    [sessions]
  );

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? Any RSVPs will be removed.`)) return;
    await adminDeleteSession(pin, s.id);
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-sessions-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-[var(--lc-muted)]">
          {sessions.length} session{sessions.length === 1 ? "" : "s"} · {totalRsvps} RSVP{totalRsvps === 1 ? "" : "s"} total
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-[var(--lc-muted)] cursor-pointer">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(e) => setIncludePast(e.target.checked)}
              className="accent-[var(--lc-moss)]"
              data-testid="session-include-past"
            />
            Show past sessions
          </label>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)]"
            data-testid="session-create-open"
          >
            <Plus className="h-3.5 w-3.5" /> New session
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-8 flex items-center gap-2 text-sm text-[var(--lc-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-10 text-center">
          <CalendarDays className="h-7 w-7 mx-auto text-[var(--lc-muted)] mb-2" />
          <div className="font-serif-display text-xl">No sessions scheduled</div>
          <p className="text-sm text-[var(--lc-muted)] mt-1">Create a 1:1 slot or group class to let members RSVP.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => {
            const start = new Date(s.start_at);
            const end = new Date(s.end_at);
            return (
              <article key={s.id} className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6" data-testid={`admin-session-${s.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-serif-display text-2xl leading-tight">{s.title}</h3>
                      <span className="eyebrow px-2 py-0.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm">
                        {s.type === "group" ? "Group" : "1 : 1"}
                      </span>
                      {s.status !== "open" && (
                        <span className="eyebrow px-2 py-0.5 bg-neutral-200 text-neutral-700 rounded-sm">
                          {s.status}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--lc-muted)]">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {start.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        {" – "}
                        {end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {s.location && (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" /> {s.location}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        {s.rsvp_count}/{s.capacity} confirmed
                        {s.waitlist_count > 0 && (
                          <span className="ml-2 text-amber-700 font-semibold">· {s.waitlist_count} waitlisted</span>
                        )}
                      </span>
                    </div>
                    {s.notes && (
                      <p className="mt-3 text-sm text-[var(--lc-ink)] bg-[var(--lc-cream-soft)] border border-[var(--lc-line)] rounded-sm p-3 whitespace-pre-wrap">
                        {s.notes}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => remove(s)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.14em] uppercase hover:border-red-500 hover:text-red-700"
                    data-testid={`admin-session-delete-${s.id}`}
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>

                {s.rsvps.length > 0 && (
                  <div className="mt-4 border-t border-[var(--lc-line)] pt-4">
                    <div className="eyebrow text-[var(--lc-muted)] mb-2">Confirmed members</div>
                    <ul className="divide-y divide-[var(--lc-line)]">
                      {s.rsvps.map((r) => (
                        <li key={r.client_id} className="py-2 flex items-center justify-between gap-3 text-sm">
                          <span className="font-semibold">{r.client_name}</span>
                          <span className="flex items-center gap-3 text-xs text-[var(--lc-muted)]">
                            {r.client_email && (
                              <a href={`mailto:${r.client_email}`} className="inline-flex items-center gap-1 hover:text-[var(--lc-ink)]">
                                <Mail className="h-3 w-3" /> {r.client_email}
                              </a>
                            )}
                            RSVPed {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {s.waitlist && s.waitlist.length > 0 && (
                  <div className="mt-3 border-t border-dashed border-[var(--lc-line)] pt-3" data-testid={`admin-session-waitlist-${s.id}`}>
                    <div className="eyebrow text-amber-800 mb-2">Waitlist · auto-promoted on cancel</div>
                    <ol className="divide-y divide-[var(--lc-line)]">
                      {s.waitlist.map((r, i) => (
                        <li key={r.client_id} className="py-2 flex items-center justify-between gap-3 text-sm">
                          <span>
                            <span className="eyebrow text-[var(--lc-muted)] mr-2">#{i + 1}</span>
                            <span className="font-semibold">{r.client_name}</span>
                          </span>
                          <span className="flex items-center gap-3 text-xs text-[var(--lc-muted)]">
                            {r.client_email && (
                              <a href={`mailto:${r.client_email}`} className="inline-flex items-center gap-1 hover:text-[var(--lc-ink)]">
                                <Mail className="h-3 w-3" /> {r.client_email}
                              </a>
                            )}
                            Joined {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateSessionModal
          pin={pin}
          onClose={() => setShowCreate(false)}
          onCreated={load}
        />
      )}
    </div>
  );
}
